import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type RegistrationData = {
  email: string;
  password: string;
};

const registrationContext = createContext<{
  step: number;
  data: RegistrationData;
  nextStep: (data: RegistrationData, onlyUpdateState?: boolean) => void;
  prevStep: () => void;
  updateDataHandler: (data: RegistrationData) => void;
}>({
  step: 0,
  data: {
    email: '',
    password: '',
  },
  nextStep: () => {},
  prevStep: () => {},
  updateDataHandler:() => {},
});

export const useRegistrationContext = () => {
  return useContext(registrationContext);
};

interface IProps extends PropsWithChildren {
  isModalOpened: boolean;
}

export const RegistrationContextProvider = ({ children, isModalOpened }: IProps) => {
  const [registrationContextState, setRegistrationContextState] = useState<{
    step: number;
    data: RegistrationData;
  }>({
    step: 0,
    data: {
      email: '',
      password: '',
    },
  });

  const nextStep = useCallback((data: RegistrationData) => {
    setRegistrationContextState((prev) => ({
      step: prev.step + 1,
      data: { ...prev.data, ...data },
    }));
  }, []);

  const prevStep = useCallback(() => {
    setRegistrationContextState((prev) => ({
      step: prev.step - 1,
      data: { ...prev.data },
    }));
  }, []);

  useEffect(() => {
    if (!isModalOpened) {
      setRegistrationContextState({
        step: 0,
        data: {
          email: '',
          password: '',
        },
      });
    }
  }, [isModalOpened]);

  const updateDataHandler = useCallback((data: RegistrationData) => {
    setRegistrationContextState((prev) => ({
      ...prev,
      data: { ...prev.data, ...data },
    }));
  }, [])

  return (
    <registrationContext.Provider
      value={useMemo(
        () => ({ ...registrationContextState, nextStep, prevStep, updateDataHandler }),
        [registrationContextState, nextStep, prevStep, updateDataHandler],
      )}
    >
      {children}
    </registrationContext.Provider>
  );
};
